import React, { useState } from "react";
import { ShieldCheck, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { legalApi } from "@/services/api";
import { apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";

/**
 * Blocking re-acceptance gate. Rendered inside the authenticated layouts.
 * Appears only when the backend flags `user.legal.needs_acceptance` — i.e.
 * an in-scope user (company owner/admin or client-portal user) whose accepted
 * versions are missing or out of date after a policy update.
 */
export default function LegalGate() {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);

  if (!user?.legal?.needs_acceptance) return null;

  const accept = async () => {
    setSaving(true);
    try {
      await legalApi.accept();
      await refreshUser();
      toast.success("Thanks — your acceptance has been recorded.");
    } catch (err) {
      toast.error(apiError(err, "Could not record your acceptance."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="legal-gate">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h2 className="text-center text-xl font-bold text-slate-900">We've updated our legal terms</h2>
        <p className="mt-2 text-center text-sm leading-relaxed text-slate-600">
          Please review our Privacy Policy and Terms of Use, then accept to continue using FD Check.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <a href="/legal/privacy" target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="legal-gate-privacy">
            Privacy Policy <ExternalLink className="h-4 w-4 text-slate-400" />
          </a>
          <a href="/legal/terms" target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="legal-gate-terms">
            Terms of Use <ExternalLink className="h-4 w-4 text-slate-400" />
          </a>
        </div>
        <Button className="mt-5 w-full" size="lg" onClick={accept} disabled={saving} data-testid="legal-gate-accept">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          I agree and accept
        </Button>
      </div>
    </div>
  );
}
